import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Editor } from "primereact/editor";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import axios from "axios";
import { Api } from "../../../services/api";
import './EmailDialog.css'

const EmailDialog = ({
  visible,
  onHide,
  showName,
  showId,
  allSupervisors,
  toast,
}) => {
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [selectedCCList, setSelectedCCList] = useState([]);
  const [emailThreads, setEmailThreads] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null); // stores thread/message being replied to
  const [expandedMessages, setExpandedMessages] = useState({});

  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (visible && showId) {
      fetchEmailThreads(showId);
      setReplyingTo(null); // reset reply when dialog opens
    }
  }, [visible, showId]);

  const fetchEmailThreads = async (leadId) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      };
      const res = await axios.get(
        `${Api.API_BASE_URL}crm/get-lead-mail/${leadId}`,
        { headers }
      );
      setEmailThreads(res.data.result || []);
    } catch (error) {
      console.error("Error fetching email history:", error);
      setEmailThreads([]);
    }
  };

  const toggleExpand = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!customSubject || !customMessage) return;

    const emailData = {
      id: showId,
      to: showName,
      subject: customSubject,
      message: customMessage,
      cc: selectedCCList.map((user) => user.user_id),
      in_reply_to: replyingTo?.messages?.slice(-1)[0]?.message_id || null,
      thread_number: replyingTo?.thread_number || null,
    };

    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      };
      const response = await axios.post(
        Api.API_BASE_URL + "crm/send-lead-mail",
        emailData,
        { headers }
      );

      toast.current.show({
        severity: "success",
        detail: response.data.message || "Email sent successfully",
        life: 3000,
      });

      // Append new message to the thread or create new thread
      const newMessage = {
        _id: Date.now(),
        lead_id: showId,
        cc_list: selectedCCList.map((u) => u.user_id),
        message: customMessage,
        subject: customSubject,
        direction: "outgoing",
        from_email: "You",
        to_email: [showName],
        message_id: response.data.message_id || null,
        send_at: new Date().toISOString(),
      };

      if (replyingTo) {
        // add to existing thread
        const updatedThreads = emailThreads.map((thread) =>
          thread.thread_number === replyingTo.thread_number
            ? { ...thread, messages: [...thread.messages, newMessage] }
            : thread
        );
        setEmailThreads(updatedThreads);
      } else {
        // new thread
        const newThread = {
          thread_number:
            emailThreads.length > 0
              ? Math.max(...emailThreads.map((t) => t.thread_number)) + 1
              : 1,
          subject: customSubject,
          messages: [newMessage],
        };
        setEmailThreads([newThread, ...emailThreads]);
      }

      // Reset draft box
      setCustomSubject("");
      setCustomMessage("");
      setSelectedCCList([]);
      setReplyingTo(null);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.current.show({
        severity: "error",
        detail: "An error occurred while sending the email.",
        life: 3000,
      });
    }
  };

  const dialogHeader = (
    <div className="flex align-items-center gap-2">
      <i className="pi pi-envelope text-primary text-xl"></i>
      <span className="text-xl font-semibold text-900">
        Email Correspondence with {showName}
      </span>
    </div>
  );

  const handleGenerateDraft = async () => {
    if (!aiPrompt) return;
    setIsGenerating(true);

    try {
      const response = await fetch("/api/openai-draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: aiPrompt }),
      });

      const data = await response.json();
      if (data?.draft) {
        setCustomMessage(data.draft); // populate the Editor with AI draft
      }
    } catch (error) {
      console.error("Error generating AI draft:", error);
    } finally {
      setIsGenerating(false);
    }
  };


  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{ width: "60vw", maxWidth: "900px" }}
      dismissableMask
      onHide={onHide}
      className="email-dialog dashboard-dialog"
      draggable={false}
      resizable={false}
    >
      <div className="email-dialog-container">
        {/* Show draft box if no previous emails or replying */}
        {(!emailThreads.length || replyingTo) && (
          <form
            onSubmit={handleSendEmail}
            className="flex flex-column gap-3 mb-4 p-4 border-1 border-round-lg surface-border bg-white shadow-sm"
          >
            <h4 className="m-0 text-900 font-bold text-xl">Compose Email</h4>
            
            {/* To Field */}
            <div className="flex flex-column">
              <label className="font-medium text-700 mb-2">
                To
              </label>
              <InputText value={showName} disabled className="w-full bg-gray-50 custom-input-height" />
            </div>

            {/* CC Field */}
            <div className="flex flex-column">
              <label className="font-medium text-700 mb-2">CC</label>
              <MultiSelect
                value={selectedCCList}
                onChange={(e) => setSelectedCCList(e.value)}
                options={allSupervisors}
                optionLabel={(e) =>
                  `${e.first_name} ${e.last_name} - ${e.role_name}`
                }
                display="chip"
                filter
                placeholder="Select CC recipients"
                className="w-full custom-input-height"
                pt={{
                    root: { style: { minHeight: '42px' } }
                }}
              />
            </div>

            {/* Subject */}
            <div className="flex flex-column">
              <label
                htmlFor="customSubject"
                className="font-medium text-700 mb-2"
              >
                Subject
              </label>

              <InputText
                id="customSubject"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Enter email subject"
                className="w-full custom-input-height"
              />
            </div>

            {/* AI Prompt Input */}
            <div className="flex flex-column p-3 surface-50 border-round-md border-1 surface-border">
              <label
                htmlFor="aiPrompt"
                className="font-medium text-700 mb-2 flex align-items-center gap-2"
              >
                <i className="pi pi-sparkles text-primary"></i>
                AI Assistant
              </label>
              <div className="flex gap-2">
                <InputText
                    id="aiPrompt"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Describe what you want to say..."
                    className="w-full custom-input-height"
                />
                <Button
                  label={isGenerating ? "Generating..." : "Draft"}
                  icon="pi pi-bolt"
                  disabled={!aiPrompt || isGenerating}
                  className="customSave-button white-space-nowrap"
                  onClick={handleGenerateDraft}
                />
              </div>
            </div>

            {/* Message Editor */}
            <div className="flex flex-column">
              <label
                htmlFor="customMessage"
                className="font-medium text-700 mb-2"
              >
                Message
              </label>
              <Editor
                id="customMessage"
                value={customMessage}
                onTextChange={(e) => setCustomMessage(e.htmlValue)}
                style={{
                  height: "250px",
                }}
                pt={{
                    content: { style: { borderRadius: '0 0 6px 6px' } },
                    toolbar: { style: { borderRadius: '6px 6px 0 0' } }
                }}
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-content-end gap-2 pt-2 border-top-1 surface-border">
              <Button
                label="Cancel"
                type="button"
                outlined
                severity="secondary"
                onClick={() => setReplyingTo(null)}
                className="px-4"
              />
              <Button
                label="Send Email"
                icon="pi pi-send"
                type="submit"
                className="customSave-button px-4"
                disabled={!customSubject || !customMessage}
              />
            </div>
          </form>
        )}

        {/* Divider */}
        {emailThreads.length > 0 && <div className="flex align-items-center gap-3 my-4">
            <div className="flex-grow-1 border-top-1 surface-border"></div>
            <span className="text-500 font-medium text-sm">HISTORY</span>
            <div className="flex-grow-1 border-top-1 surface-border"></div>
        </div>}

        {/* Email Threads */}
        {emailThreads.length === 0 ? (
          <div className="text-center p-5 surface-50 border-round-md">
            <i className="pi pi-inbox text-4xl text-400 mb-3"></i>
            <p className="text-500 m-0">No previous email history found.</p>
          </div>
        ) : (
          <div className="email-history-container max-h-30rem overflow-auto">
            {[...emailThreads].reverse().map((thread) => (
              <div key={thread.thread_number} className="email-thread-card">
                <div className="flex align-items-center justify-content-between mb-3 pb-2 border-bottom-1 surface-border">
                    <h6 className="m-0 text-900 font-bold text-lg">
                    {thread.subject}
                    </h6>
                    <span className="text-sm text-500 bg-gray-100 px-2 py-1 border-round">Thread #{thread.thread_number}</span>
                </div>

                {[...thread.messages].reverse().map((msg, i) => (
                  <div
                    key={i}
                    className={`email-message-card ${msg.direction === "incoming" ? "incoming" : "outgoing"}`}
                  >
                    <div className="email-meta">
                      <span className="font-medium text-700">
                        {msg.direction === "outgoing" ? "You sent to " : "Received from "}
                        <span className="text-900 font-semibold">
                            {msg.direction === "outgoing"
                            ? msg.to_email.join(", ")
                            : msg.from_email}
                        </span>
                      </span>
                      <span className="text-sm">{new Date(msg.send_at).toLocaleString()}</span>
                    </div>

                    <div className="email-body">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: expandedMessages[msg._id]
                            ? msg.message
                            : msg.message.split(" ").slice(0, 40).join(" ") +
                              (msg.message.split(" ").length > 40 ? "..." : ""),
                        }}
                      />

                      {msg.message.split(" ").length > 40 && (
                        <Button
                          label={
                            expandedMessages[msg._id]
                              ? "Show less"
                              : "Show more"
                          }
                          link
                          className="p-0 text-sm mt-2"
                          onClick={() => toggleExpand(msg._id)}
                        />
                      )}
                    </div>

                    {/* ✅ Reply button next to latest message only */}
                    {!replyingTo && i === 0 && (
                      <div className="mt-3 pt-2 border-top-1 surface-border text-right">
                        <Button
                          label="Reply to Thread"
                          icon="pi pi-reply"
                          size="small"
                          outlined
                          onClick={() => {
                            setReplyingTo(thread);
                            setCustomSubject(`Re: ${thread.subject}`);
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default EmailDialog;
